/* eslint-disable react-refresh/only-export-components */

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { PRODUCTS as LOCAL_PRODUCTS } from '../data/products'
import { isStagingHost } from '../utils/stagingAccess'

const resolveCatalogEndpoint = () => {
  if (import.meta.env.VITE_CATALOG_ENDPOINT) return import.meta.env.VITE_CATALOG_ENDPOINT
  const newsletter = import.meta.env.VITE_NEWSLETTER_ENDPOINT
  if (typeof newsletter === 'string' && newsletter.includes('/api/newsletter')) {
    return newsletter.replace(/\/api\/newsletter\/?$/, '/api/catalog')
  }
  return '/api/catalog'
}

const CATALOG_ENDPOINT = resolveCatalogEndpoint()

const isBrowserStaging = () =>
  typeof window !== 'undefined' && isStagingHost(window.location.hostname)

const parseShopEnabled = (value) =>
  value === true || value === 1 || value === '1' || value === 'true'

const defaultCatalog = {
  products: [],
  shopEnabled: false,
  catalogReady: false,
  getProductById: () => null,
  getProductBySlug: () => null,
}

const CatalogContext = createContext(defaultCatalog)

function mergeCatalog(apiProducts) {
  if (!Array.isArray(apiProducts) || apiProducts.length === 0) return LOCAL_PRODUCTS

  const localById = new Map(LOCAL_PRODUCTS.map((item) => [item.id, item]))
  const merged = apiProducts.map((api) => {
    const local = localById.get(api.id) || {}
    return {
      ...local,
      id: api.id,
      slug: api.slug || local.slug,
      price: Number(api.priceCents) / 100,
      priceExclTax: Number(api.priceExclTaxCents) / 100,
      stock: Number(api.stock),
      category: api.category || local.category || 'stickers',
      size: api.size || local.size,
      material: api.material || local.material,
      featured: Boolean(api.featured ?? local.featured),
      images: Array.isArray(api.images) && api.images.length > 0 ? api.images : local.images,
      motifs: local.motifs || [],
      accent: local.accent,
      glow: local.glow,
    }
  })

  LOCAL_PRODUCTS.forEach((local) => {
    if (!merged.some((item) => item.id === local.id)) merged.push(local)
  })

  return merged
}

export function CatalogProvider({ children }) {
  const stagingFallback = isBrowserStaging()
  const [products, setProducts] = useState(() => (stagingFallback ? LOCAL_PRODUCTS : []))
  const [shopEnabled, setShopEnabled] = useState(() => stagingFallback)
  const [catalogReady, setCatalogReady] = useState(false)

  useEffect(() => {
    const abort = new AbortController()
    const allowStagingFallback = isBrowserStaging()

    const applyUnavailable = () => {
      if (allowStagingFallback) {
        setShopEnabled(true)
        setProducts(LOCAL_PRODUCTS)
        return
      }
      setShopEnabled(false)
      setProducts([])
    }

    fetch(CATALOG_ENDPOINT, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
      signal: abort.signal,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (abort.signal.aborted) return
        if (!payload) {
          applyUnavailable()
          return
        }
        const enabled = parseShopEnabled(payload.shopEnabled)
        setShopEnabled(enabled)
        setProducts(enabled ? mergeCatalog(payload.products) : [])
      })
      .catch(() => {
        if (abort.signal.aborted) return
        applyUnavailable()
      })
      .finally(() => {
        if (!abort.signal.aborted) setCatalogReady(true)
      })

    return () => abort.abort()
  }, [])

  const value = useMemo(() => ({
    products,
    shopEnabled,
    catalogReady,
    getProductById: (id) => products.find((item) => item.id === id) || null,
    getProductBySlug: (slug) => products.find((item) => item.slug === slug) || null,
  }), [products, shopEnabled, catalogReady])

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>
}

export function useCatalog() {
  return useContext(CatalogContext)
}
